import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Filter, Zap, Users, Shield, Clock, Database, GitMerge } from 'lucide-react';
import { Button } from '../components/Button';
import { HeroRoutingDiagram, WorkflowSprawlDiagram } from '../components/Diagrams';
export function Home() {
  return <div className="flex flex-col w-full" data-id="element-261">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white pt-16 pb-24 lg:pt-24 lg:pb-32" data-id="element-262">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" data-id="element-263"></div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-264">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-265">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-id="element-266">
              Route every lead to the right rep, and automate the right next
              step
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="mt-6 text-lg leading-8 text-slate-600" data-id="element-267">
              DemandPath helps RevOps teams assign inbound leads, enforce
              follow-up SLAs, and automate handoffs in HubSpot with flexible,
              auditable logic.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="mt-10 flex items-center justify-center gap-x-6" data-id="element-268">
              <Button href="/book-a-demo" size="lg" data-id="element-269">
                Book a demo
              </Button>
              <Button href="/why-not-hubspot-workflows" variant="outline" size="lg" data-id="element-270">
                Compare to HubSpot workflows
              </Button>
            </motion.div>
          </div>

          <HeroRoutingDiagram data-id="element-271" />
        </div>
      </section>

      {/* Trust / Support Bar */}
      <section className="border-y border-slate-200 bg-slate-50 py-8" data-id="element-272">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-273">
          <div className="text-center mb-6" data-id="element-274">
            <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-inset ring-brand-600/20" data-id="element-275">
              Built exclusively for HubSpot
            </span>
          </div>
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-3 md:gap-x-8 text-center" data-id="element-276">
            <div className="flex flex-col items-center gap-2" data-id="element-277">
              <Filter className="h-5 w-5 text-brand-600" data-id="element-278" />
              <p className="text-sm font-medium text-slate-900" data-id="element-279">
                Advanced assignment logic without brittle workflows
              </p>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-280">
              <Users className="h-5 w-5 text-brand-600" data-id="element-281" />
              <p className="text-sm font-medium text-slate-900" data-id="element-282">
                Round robin, owner-aware routing, and rule-based handoffs
              </p>
            </div>
            <div className="flex flex-col items-center gap-2" data-id="element-283">
              <Shield className="h-5 w-5 text-brand-600" data-id="element-284" />
              <p className="text-sm font-medium text-slate-900" data-id="element-285">
                SLA enforcement, fallback logic, and full audit visibility
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white" data-id="element-286">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-287">
          <div className="mx-auto max-w-2xl lg:text-center mb-16" data-id="element-288">
            <h2 className="text-base font-semibold leading-7 text-brand-600" data-id="element-289">
              The Problem
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-290">
              HubSpot workflows work well, until lead routing gets complicated
            </p>
            <p className="mt-6 text-lg leading-8 text-slate-600" data-id="element-291">
              As inbound volume grows, lead assignment logic gets harder to
              manage. Territories change. Named accounts override round robin.
              Different forms need different rules. Existing ownership should
              sometimes win, and sometimes not. What starts as a few workflows
              turns into brittle routing logic that's hard to test, trust, and
              maintain.
            </p>
          </div>

          <WorkflowSprawlDiagram data-id="element-292" />
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-slate-50" data-id="element-293">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-294">
          <div className="mx-auto max-w-2xl lg:text-center mb-16" data-id="element-295">
            <h2 className="text-base font-semibold leading-7 text-brand-600" data-id="element-296">
              The Solution
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-297">
              Built for the real-world complexity of inbound lead assignment
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none" data-id="element-298">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-4" data-id="element-299">
              {[{
              name: 'Flexible routing logic',
              description: 'Route leads by geography, source, company size, product interest, lifecycle stage, account ownership, and more.',
              icon: Filter
            }, {
              name: 'Automated response orchestration',
              description: 'Trigger the right next step after assignment: notify reps, create tasks, enforce SLAs, and launch downstream workflows.',
              icon: Zap
            }, {
              name: 'Fair and accurate distribution',
              description: 'Support round robin, weighted assignment, named-account routing, owner-aware logic, and exceptions.',
              icon: Users
            }, {
              name: 'Visibility and control for RevOps',
              description: 'Show why every lead was assigned the way it was with routing transparency and audit trails.',
              icon: Shield
            }].map(feature => <div key={feature.name} className="flex flex-col" data-id="element-300">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-slate-900" data-id="element-301">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600" data-id="element-302">
                      <feature.icon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-303" />
                    </div>
                    {feature.name}
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-slate-600" data-id="element-304">
                    <p className="flex-auto" data-id="element-305">{feature.description}</p>
                  </dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-white border-b border-slate-200" data-id="element-306">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-307">
          <div className="mx-auto max-w-2xl lg:text-center mb-16" data-id="element-308">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-309">
              Capture signals. Apply logic. Automate action.
            </h2>
          </div>

          <div className="relative" data-id="element-310">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-slate-100 w-2/3 mx-auto z-0" data-id="element-311"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative z-10" data-id="element-312">
              <div className="text-center" data-id="element-313">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-white border-4 border-slate-50 shadow-sm mb-6" data-id="element-314">
                  <Database className="h-10 w-10 text-brand-600" data-id="element-315" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-316">
                  1. Capture inbound signals
                </h3>
                <p className="text-sm text-slate-600" data-id="element-317">
                  Listen for new leads, form submissions, or lifecycle stage
                  changes directly in HubSpot.
                </p>
              </div>
              <div className="text-center" data-id="element-318">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-white border-4 border-slate-50 shadow-sm mb-6" data-id="element-319">
                  <GitMerge className="h-10 w-10 text-brand-600" data-id="element-320" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-321">
                  2. Apply routing rules
                </h3>
                <p className="text-sm text-slate-600" data-id="element-322">
                  Evaluate leads against your global ruleset, checking
                  ownership, territory, and round robin pools.
                </p>
              </div>
              <div className="text-center" data-id="element-323">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-white border-4 border-slate-50 shadow-sm mb-6" data-id="element-324">
                  <Zap className="h-10 w-10 text-brand-600" data-id="element-325" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-326">
                  3. Trigger the next step
                </h3>
                <p className="text-sm text-slate-600" data-id="element-327">
                  Assign the owner in HubSpot, send Slack alerts, and start the
                  SLA timer automatically.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-328">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-329">
          <div className="mx-auto max-w-2xl lg:text-center mb-16" data-id="element-330">
            <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl" data-id="element-331">
              Why teams choose DemandPath over standard workflows
            </h2>
          </div>

          <div className="mx-auto max-w-4xl bg-slate-800 rounded-2xl overflow-hidden border border-slate-700" data-id="element-332">
            <div className="grid grid-cols-3 border-b border-slate-700 bg-slate-800/50" data-id="element-333">
              <div className="p-6 font-semibold text-slate-300" data-id="element-334">Capability</div>
              <div className="p-6 font-semibold text-slate-400 border-l border-slate-700" data-id="element-335">
                HubSpot Workflows
              </div>
              <div className="p-6 font-semibold text-brand-400 border-l border-slate-700" data-id="element-336">
                DemandPath
              </div>
            </div>

            {[{
            cap: 'Routing Logic',
            hs: 'Fragmented across multiple workflows',
            dp: 'Centralized, global ruleset'
          }, {
            cap: 'Round Robin',
            hs: 'Basic rotation, hard to weight or pause',
            dp: 'Advanced weighting, schedules, pauses'
          }, {
            cap: 'Owner-Aware',
            hs: 'Requires complex if/then branching',
            dp: 'Native check for existing ownership'
          }, {
            cap: 'SLA Enforcement',
            hs: 'Requires separate delay workflows',
            dp: 'Built-in timers and escalations'
          }, {
            cap: 'Auditability',
            hs: 'Digging through workflow history',
            dp: 'Clear log of why every lead routed'
          }].map((row, i) => <div key={i} className="grid grid-cols-3 border-b border-slate-700 last:border-0 hover:bg-slate-700/30 transition-colors" data-id="element-337">
                <div className="p-6 text-sm text-slate-300 flex items-center" data-id="element-338">
                  {row.cap}
                </div>
                <div className="p-6 text-sm text-slate-400 border-l border-slate-700 flex items-center" data-id="element-339">
                  {row.hs}
                </div>
                <div className="p-6 text-sm text-white border-l border-slate-700 flex items-center gap-2" data-id="element-340">
                  <CheckCircle2 className="h-4 w-4 text-brand-500 shrink-0" data-id="element-341" />
                  {row.dp}
                </div>
              </div>)}
          </div>
          <div className="mt-10 text-center" data-id="element-342">
            <Button href="/why-not-hubspot-workflows" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white" data-id="element-343">
              Read the full comparison
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases Teaser */}
      <section className="py-24 bg-slate-50" data-id="element-344">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-345">
          <div className="mb-12" data-id="element-346">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-347">
              Built for HubSpot teams with routing complexity
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-348">
            {['Multi-region sales teams', 'High inbound volume', 'SDR to AE handoffs', 'Named-account routing', 'Owner-based routing exceptions', 'Response-time SLA management'].map((useCase, i) => <div key={i} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex items-start gap-4" data-id="element-349">
                <div className="mt-1 h-2 w-2 rounded-full bg-brand-500 shrink-0" data-id="element-350"></div>
                <div className="text-base font-medium text-slate-900" data-id="element-351">
                  {useCase}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-white" data-id="element-352">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-353">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-354">
            Move beyond brittle lead routing logic
          </h2>
          <p className="mt-6 text-lg leading-8 text-slate-600 mb-10" data-id="element-355">
            See how DemandPath helps HubSpot teams assign leads faster, enforce
            response rules, and keep routing logic under control.
          </p>
          <Button href="/book-a-demo" size="lg" data-id="element-356">
            Book a demo
          </Button>
        </div>
      </section>
    </div>;
}